import { createPdfHelpers, uploadAndRegisterPdf } from './gestion-tecnica-documentos'
import { RosaAssessmentRow } from './gestion-tecnica-rosa'
import {
  calcularPuntuacionA1, calcularPuntuacionA2, calcularPuntuacionA3, calcularPuntuacionA4, calcularCeldaTablaA,
  calcularPuntuacionTelefono, calcularPuntuacionPantalla, calcularCeldaTablaB,
  calcularPuntuacionRaton, calcularPuntuacionTeclado, calcularCeldaTablaC,
  calcularPuntuacionPantallaPerifericos, calcularCeldaTablaE,
} from './rosa'

// Genera el reporte técnico PDF de una evaluación ROSA, siguiendo el mismo
// patrón que generateRebaReportPDF(): createPdfHelpers() para el flujo de
// texto + uploadAndRegisterPdf() para subir el blob y registrarlo en
// sso_documents.
//
// checklist_item_id: 'GT-06' — mismo ítem genérico que RULA/REBA (confirmado
// en compliance-engine.ts: "Informe de evaluación de riesgos ergonómicos y
// psicosociales", Decisión 584 Art. 11, DE 255 (2024) Art. 44/45/46, AM 196
// (2024)) — sin atadura a una metodología específica.
const NIVEL_ACCION_LABEL: Record<RosaAssessmentRow['resultado']['nivelAccion'], string> = {
  sin_accion_inmediata: 'Sin acción inmediata requerida',
  accion_requerida: 'Acción requerida — puntuación >=5',
}

export async function generateRosaReportPDF(
  evaluacion: RosaAssessmentRow,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const helpers = createPdfHelpers()
  const { txt, hline, doc, skip, drawTable } = helpers

  const { inputs, resultado } = evaluacion
  const { silla, pantallaTelefono, ratonTeclado } = inputs

  // a. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Reporte de Evaluación Ergonómica — Método ROSA', 12, 'bold', 'center', 0, 6)
  txt(evaluacion.descripcion, 11, 'bold', 'center', 0, 6)
  hline()

  txt('Fuente metodológica: Sonne, M., Villalta, D.L. & Andrews, D.M. (2012). Applied Ergonomics, 43(1), 98-108', 9, 'italic', 'left', 0, 4)

  // b. Sección A — Silla
  txt('Sección A — Silla', 11, 'bold', 'left', 0, 4)
  txt(
    `Altura del asiento: ${silla.alturaAsiento} — altura no regulable: ${silla.alturaNoRegulable ? 'sí' : 'no'}, espacio insuficiente bajo la mesa: ${silla.espacioInsuficienteBajoMesa ? 'sí' : 'no'}`,
    9, 'normal', 'left', 4, 2
  )
  txt(
    `Profundidad del asiento: ${silla.profundidadAsiento} — profundidad no regulable: ${silla.profundidadNoRegulable ? 'sí' : 'no'}`,
    9, 'normal', 'left', 4, 2
  )
  txt(
    `Reposabrazos: ${silla.reposabrazos} — demasiado anchos: ${silla.reposabrazosDemasiadoAnchos ? 'sí' : 'no'}, no regulables: ${silla.reposabrazosNoRegulables ? 'sí' : 'no'}, bordes duros o filosos: ${silla.reposabrazosBordesDurosOFilosos ? 'sí' : 'no'}`,
    9, 'normal', 'left', 4, 2
  )
  txt(
    `Respaldo: ${silla.respaldo} — superficie alta (hombros encogidos): ${silla.respaldoSuperficieAlta ? 'sí' : 'no'}, no regulable: ${silla.respaldoNoRegulable ? 'sí' : 'no'}`,
    9, 'normal', 'left', 4, 2
  )
  txt(`Tiempo de uso: ${silla.tiempoUso}`, 9, 'normal', 'left', 4, 3)
  txt(`Puntuación Silla: ${resultado.puntuacionSilla}`, 10, 'bold', 'left', 0, 4)
  skip(2)
  {
    const a1 = calcularPuntuacionA1(silla.alturaAsiento, silla.alturaNoRegulable, silla.espacioInsuficienteBajoMesa)
    const a2 = calcularPuntuacionA2(silla.profundidadAsiento, silla.profundidadNoRegulable)
    const a3 = calcularPuntuacionA3(silla.reposabrazos, silla.reposabrazosDemasiadoAnchos, silla.reposabrazosNoRegulables, silla.reposabrazosBordesDurosOFilosos)
    const a4 = calcularPuntuacionA4(silla.respaldo, silla.respaldoSuperficieAlta, silla.respaldoNoRegulable)
    const sumaAlturaProfundidad = a1 + a2
    const sumaReposabrazosRespaldo = a3 + a4
    drawTable({
      title: 'Tabla A — Silla (Asiento × Reposabrazos/Respaldo)',
      colHeaders: [2, 3, 4, 5, 6, 7, 8, 9].map(c => `R+R ${c}`),
      rowHeaders: [2, 3, 4, 5, 6, 7, 8].map(r => `Asiento ${r}`),
      data: [2, 3, 4, 5, 6, 7, 8].map(fila => [2, 3, 4, 5, 6, 7, 8, 9].map(col => calcularCeldaTablaA(fila, col))),
      highlightRow: sumaAlturaProfundidad - 2,
      highlightCol: sumaReposabrazosRespaldo - 2,
    })
  }
  hline()

  // c. Sección B — Teléfono y Pantalla
  txt('Sección B — Teléfono y Pantalla', 11, 'bold', 'left', 0, 4)
  txt(
    `Teléfono: ${pantallaTelefono.telefono} — sujeción hombro-cuello: ${pantallaTelefono.telefonoSujecionHombroCuello ? 'sí' : 'no'}, sin manos libres: ${pantallaTelefono.telefonoSinManosLibres ? 'sí' : 'no'}, tiempo de uso: ${pantallaTelefono.telefonoTiempoUso}`,
    9, 'normal', 'left', 4, 2
  )
  txt(
    `Pantalla: ${pantallaTelefono.pantalla} — distancia >75cm: ${pantallaTelefono.pantallaDistanciaMayor75cm ? 'sí' : 'no'}, giro de cuello: ${pantallaTelefono.pantallaGiroCuello ? 'sí' : 'no'}, sin portadocumentos: ${pantallaTelefono.pantallaSinPortadocumentos ? 'sí' : 'no'}, reflejos: ${pantallaTelefono.pantallaReflejos ? 'sí' : 'no'}, tiempo de uso: ${pantallaTelefono.pantallaTiempoUso}`,
    9, 'normal', 'left', 4, 3
  )
  txt(`Puntuación Sección B: ${resultado.puntuacionSeccionB}`, 10, 'bold', 'left', 0, 4)
  skip(2)
  {
    const telefonoScore = calcularPuntuacionTelefono(pantallaTelefono)
    const pantallaScore = calcularPuntuacionPantalla(pantallaTelefono)
    drawTable({
      title: 'Tabla B — Teléfono × Pantalla',
      colHeaders: [0, 1, 2, 3, 4, 5, 6, 7, 8].map(c => `Pantalla ${c}`),
      rowHeaders: [0, 1, 2, 3, 4, 5, 6].map(r => `Teléfono ${r}`),
      data: [0, 1, 2, 3, 4, 5, 6].map(t => [0, 1, 2, 3, 4, 5, 6, 7, 8].map(p => calcularCeldaTablaB(t, p))),
      highlightRow: telefonoScore,
      highlightCol: pantallaScore,
    })
  }
  hline()

  // d. Sección C — Ratón y Teclado
  txt('Sección C — Ratón y Teclado', 11, 'bold', 'left', 0, 4)
  txt(
    `Ratón: ${ratonTeclado.raton} — agarre pequeño en pinza: ${ratonTeclado.ratonPequenoAgarrePinza ? 'sí' : 'no'}, ratón y teclado a diferentes alturas: ${ratonTeclado.ratonTecladoDiferentesAlturas ? 'sí' : 'no'}, reposamanos duro: ${ratonTeclado.ratonReposamanosDuro ? 'sí' : 'no'}, tiempo de uso: ${ratonTeclado.ratonTiempoUso}`,
    9, 'normal', 'left', 4, 2
  )
  txt(
    `Teclado: ${ratonTeclado.teclado} — desviación al escribir: ${ratonTeclado.tecladoDesviacionAlEscribir ? 'sí' : 'no'}, elevado/hombros encogidos: ${ratonTeclado.tecladoElevadoHombrosEncogidos ? 'sí' : 'no'}, alcance por encima de la cabeza: ${ratonTeclado.tecladoAlcancePorEncimaCabeza ? 'sí' : 'no'}, soporte no ajustable: ${ratonTeclado.tecladoSoporteNoAjustable ? 'sí' : 'no'}, tiempo de uso: ${ratonTeclado.tecladoTiempoUso}`,
    9, 'normal', 'left', 4, 3
  )
  txt(`Puntuación Sección C: ${resultado.puntuacionSeccionC}`, 10, 'bold', 'left', 0, 4)
  skip(2)
  {
    const ratonScore = calcularPuntuacionRaton(ratonTeclado)
    const tecladoScore = calcularPuntuacionTeclado(ratonTeclado)
    drawTable({
      title: 'Tabla C — Ratón × Teclado',
      colHeaders: [0, 1, 2, 3, 4, 5, 6, 7].map(c => `Teclado ${c}`),
      rowHeaders: [0, 1, 2, 3, 4, 5, 6, 7].map(r => `Ratón ${r}`),
      data: [0, 1, 2, 3, 4, 5, 6, 7].map(r => [0, 1, 2, 3, 4, 5, 6, 7].map(c => calcularCeldaTablaC(r, c))),
      highlightRow: ratonScore,
      highlightCol: tecladoScore,
    })
  }
  hline()

  // e. Resultado
  txt('Resultado', 11, 'bold', 'left', 0, 4)
  txt(`Puntuación Pantalla/Periféricos (Tabla D): ${resultado.puntuacionPantallaPerifericos}`, 9, 'normal', 'left', 0, 3)
  skip(2)
  drawTable({
    title: 'Tabla D — Pantalla/Periféricos (Sección B × Sección C)',
    colHeaders: [1, 2, 3, 4, 5, 6, 7, 8, 9].map(c => `Secc. C ${c}`),
    rowHeaders: [1, 2, 3, 4, 5, 6, 7, 8, 9].map(r => `Secc. B ${r}`),
    data: [1, 2, 3, 4, 5, 6, 7, 8, 9].map(b => [1, 2, 3, 4, 5, 6, 7, 8, 9].map(c => calcularPuntuacionPantallaPerifericos(b, c))),
    highlightRow: resultado.puntuacionSeccionB - 1,
    highlightCol: resultado.puntuacionSeccionC - 1,
  })
  txt(`Puntuación Final ROSA: ${resultado.puntuacionFinal}`, 14, 'bold', 'left', 0, 4)
  skip(2)
  drawTable({
    title: 'Tabla E — Puntuación Final (Silla × Pantalla/Periféricos)',
    colHeaders: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(c => `P/P ${c}`),
    rowHeaders: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(r => `Silla ${r}`),
    data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(s => [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(p => calcularCeldaTablaE(s, p))),
    highlightRow: resultado.puntuacionSilla - 1,
    highlightCol: resultado.puntuacionPantallaPerifericos - 1,
  })
  txt(`Nivel de actuación: ${NIVEL_ACCION_LABEL[resultado.nivelAccion]}`, 10, 'bold', 'left', 0, 4)
  hline()

  // f. Disclaimer — mismo criterio que RULA/REBA: el PDF documenta
  // exactamente lo que ve el usuario en pantalla, no una versión distinta.
  txt(
    'Método y tablas de combinación según Sonne, M., Villalta, D.L. & Andrews, D.M. (2012). ' +
    'Development and evaluation of an office ergonomic risk checklist: the Rapid Office Strain ' +
    'Assessment (ROSA). Applied Ergonomics, 43(1), 98-108. Una puntuación final >=5 indica ' +
    'necesidad de intervención según el método original. ' +
    'Este reporte no reemplaza el juicio de un profesional de ergonomía para el rediseño del puesto.',
    8, 'italic', 'left', 0, 4
  )

  const blob = doc.output('blob')
  const filename = `ROSA_${evaluacion.descripcion.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${evaluacion.id}-rosa`, blob, filename, {
    category: 'procedimiento',
    title: `Reporte ROSA - ${evaluacion.descripcion}`,
    description: 'Generado automáticamente desde Metodología ROSA',
    checklist_item_id: 'GT-06',
    uploaded_by: uploadedBy,
  })
}
